import axios, {AxiosResponse} from 'axios'
import React, {useEffect, useMemo, useState} from 'react'
import ReactApexChart from 'react-apexcharts'
import {ApexOptions} from 'apexcharts'
import {PageTitle} from '../../../_metronic/layout/core'

const API_URL = process.env.REACT_APP_API_URL
const CUSTOM_URL = `${API_URL}/Custom`
const SEMESTER_URL = `${API_URL}/Semester`

interface SemesterOption {
  id: number
  name?: string | null
  year?: number | null
  period?: number | null
  isActive?: boolean | null
  isactive?: boolean | null
}

interface ExecutiveReportSummary {
  totalQuestionCount: number
  selectedSemesterQuestionCount: number
  coursesWithQuestionCount: number
  usedQuestionCount: number
  blacklistedQuestionCount: number
  surveyCourseCount: number
  surveyAssignedCourseCount: number
  surveyReturnedCourseCount: number
  surveyNotReturnedCourseCount: number
  surveyCompletedApplicationCount: number
  surveyPendingApplicationCount: number
  surveyResponseRate: number
  classroomCount: number
  usedClassroomCount: number
  courseScheduleSlotCount: number
  averageClassroomUsageRate: number
  eventAssignmentCount: number
}

interface QuestionBreakdown {
  facultyId?: number | null
  facultyName: string
  departmentId?: number | null
  departmentName: string
  courseId: number
  courseCode: string
  courseName: string
  questionCount: number
  selectedSemesterQuestionCount: number
  usedQuestionCount: number
  blacklistedQuestionCount: number
}

interface SurveyCourse {
  semesterCourseId: number
  courseId: number
  courseCode: string
  courseName: string
  facultyId?: number | null
  facultyName: string
  departmentId?: number | null
  departmentName: string
  gradeLevel?: number | null
  totalApplications: number
  completedApplications: number
  pendingApplications: number
  responseRate: number
  hasAnyResponse: boolean
  hasNoResponse: boolean
}

interface ClassroomUsage {
  classroomId: number
  classroomName: string
  buildingName: string
  capacity: number
  examCapacity: number
  scheduledSlotCount: number
  eventCount: number
  totalAssignmentCount: number
  usageRate: number
}

interface ClassEventAssignment {
  classroomId: number
  classroomName: string
  organizationId?: number | null
  organizationName: string
  facultyId?: number | null
  facultyName: string
  gradeLevel?: number | null
  gradeLevelName: string
  eventCount: number
}

interface ExecutiveReportDashboard {
  semesterId?: number | null
  semesterName?: string | null
  generatedAt?: string | null
  summary: ExecutiveReportSummary
  questionBreakdowns: QuestionBreakdown[]
  surveyCourses: SurveyCourse[]
  classroomUsage: ClassroomUsage[]
  classEventAssignments: ClassEventAssignment[]
}

interface ChartPoint {
  label: string
  value: number
}

const emptySummary: ExecutiveReportSummary = {
  totalQuestionCount: 0,
  selectedSemesterQuestionCount: 0,
  coursesWithQuestionCount: 0,
  usedQuestionCount: 0,
  blacklistedQuestionCount: 0,
  surveyCourseCount: 0,
  surveyAssignedCourseCount: 0,
  surveyReturnedCourseCount: 0,
  surveyNotReturnedCourseCount: 0,
  surveyCompletedApplicationCount: 0,
  surveyPendingApplicationCount: 0,
  surveyResponseRate: 0,
  classroomCount: 0,
  usedClassroomCount: 0,
  courseScheduleSlotCount: 0,
  averageClassroomUsageRate: 0,
  eventAssignmentCount: 0,
}

const numberFormatter = new Intl.NumberFormat('tr-TR')
const percentFormatter = new Intl.NumberFormat('tr-TR', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
})

function formatNumber(value?: number | null) {
  return numberFormatter.format(value ?? 0)
}

function formatPercent(value?: number | null) {
  return `${percentFormatter.format(value ?? 0)}%`
}

function aggregateBy<T>(
  items: T[],
  getLabel: (item: T) => string,
  getValue: (item: T) => number
): ChartPoint[] {
  const values: Record<string, number> = {}

  items.forEach((item) => {
    const label = getLabel(item) || 'Tanımsız'
    values[label] = (values[label] || 0) + getValue(item)
  })

  return Object.keys(values)
    .map((label) => ({label, value: values[label]}))
    .sort((left, right) => right.value - left.value)
}

function barOptions(labels: string[], horizontal = false): ApexOptions {
  return {
    chart: {
      fontFamily: 'inherit',
      toolbar: {show: false},
    },
    colors: ['#2563eb'],
    dataLabels: {enabled: false},
    grid: {
      borderColor: '#eff2f5',
      strokeDashArray: 4,
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        horizontal,
      },
    },
    tooltip: {
      y: {
        formatter: (value: number) => formatNumber(value),
      },
    },
    xaxis: {
      categories: labels,
      labels: {
        trim: true,
        style: {fontSize: '11px'},
      },
    },
    yaxis: {
      labels: {
        style: {fontSize: '11px'},
      },
    },
  }
}

function donutOptions(labels: string[]): ApexOptions {
  return {
    chart: {
      fontFamily: 'inherit',
      toolbar: {show: false},
    },
    colors: ['#16a34a', '#f59e0b', '#dc2626'],
    dataLabels: {
      formatter: (value: number) => `${value.toFixed(0)}%`,
    },
    labels,
    legend: {
      position: 'bottom',
    },
    stroke: {
      width: 2,
    },
    tooltip: {
      y: {
        formatter: (value: number) => formatNumber(value),
      },
    },
  }
}

const EmptyState: React.FC<{label: string}> = ({label}) => (
  <div className='d-flex align-items-center justify-content-center text-muted min-h-200px'>
    {label}
  </div>
)

const SummaryCard: React.FC<{
  title: string
  value: string
  detail: string
  icon: string
  tone: string
}> = ({title, value, detail, icon, tone}) => (
  <div className='col-xl-3 col-md-6'>
    <div className='card card-bordered h-100'>
      <div className='card-body d-flex align-items-center gap-4'>
        <div className={`symbol symbol-50px ${tone}`}>
          <span className='symbol-label bg-transparent'>
            <i className={`${icon} fs-2`}></i>
          </span>
        </div>
        <div>
          <div className='text-muted fw-semibold fs-7'>{title}</div>
          <div className='fs-2hx fw-bold text-gray-900 lh-1'>{value}</div>
          <div className='text-gray-600 fs-8 mt-1'>{detail}</div>
        </div>
      </div>
    </div>
  </div>
)

const ChartCard: React.FC<{title: string; children: React.ReactNode}> = ({title, children}) => (
  <div className='card card-bordered h-100'>
    <div className='card-header border-0 pt-5'>
      <h3 className='card-title align-items-start flex-column'>
        <span className='card-label fw-bold text-gray-900'>{title}</span>
      </h3>
    </div>
    <div className='card-body pt-0'>{children}</div>
  </div>
)

const ExecutiveReportDashboardPage: React.FC = () => {
  const [semesters, setSemesters] = useState<SemesterOption[]>([])
  const [selectedSemesterId, setSelectedSemesterId] = useState<number | null>(null)
  const [data, setData] = useState<ExecutiveReportDashboard | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [refreshIndex, setRefreshIndex] = useState(0)

  useEffect(() => {
    axios
      .get(`${SEMESTER_URL}/getAll?page=1&items_per_page=99999&select=Id,Name,Year,Period,isActive`)
      .then((response: AxiosResponse<{data?: SemesterOption[]}>) => {
        const rows = response.data?.data ?? []
        const sortedRows = rows
          .slice()
          .sort(
            (left, right) =>
              (right.year ?? 0) - (left.year ?? 0) ||
              (right.period ?? 0) - (left.period ?? 0) ||
              right.id - left.id
          )
        setSemesters(sortedRows)
      })
      .catch(() => setSemesters([]))
  }, [])

  useEffect(() => {
    setLoading(true)
    setError(null)

    axios
      .get(`${CUSTOM_URL}/GetExecutiveReportDashboard`, {
        params: selectedSemesterId ? {semesterId: selectedSemesterId} : undefined,
      })
      .then((response: AxiosResponse<ExecutiveReportDashboard>) => {
        setData(response.data)
        if (!selectedSemesterId && response.data.semesterId) {
          setSelectedSemesterId(response.data.semesterId)
        }
      })
      .catch((requestError) => {
        const message =
          requestError?.response?.status === 403
            ? 'Bu ekran için yetkiniz bulunmuyor.'
            : 'Rapor verileri alınamadı.'
        setError(message)
      })
      .finally(() => setLoading(false))
  }, [selectedSemesterId, refreshIndex])

  const summary = data?.summary ?? emptySummary
  const questionBreakdowns = data?.questionBreakdowns ?? []
  const surveyCourses = data?.surveyCourses ?? []
  const classroomUsage = data?.classroomUsage ?? []
  const classEventAssignments = data?.classEventAssignments ?? []

  const questionFacultyPoints = useMemo(
    () => aggregateBy(questionBreakdowns, (item) => item.facultyName, (item) => item.questionCount).slice(0, 10),
    [questionBreakdowns]
  )

  const topQuestionCoursePoints = useMemo(
    () =>
      questionBreakdowns
        .slice()
        .sort((left, right) => right.questionCount - left.questionCount)
        .slice(0, 10)
        .map((item) => ({
          label: `${item.courseCode ? `${item.courseCode} - ` : ''}${item.courseName}`,
          value: item.questionCount,
        })),
    [questionBreakdowns]
  )

  const topClassroomUsagePoints = useMemo(
    () =>
      classroomUsage
        .filter((item) => item.totalAssignmentCount > 0)
        .slice()
        .sort((left, right) => right.totalAssignmentCount - left.totalAssignmentCount)
        .slice(0, 10)
        .map((item) => ({label: item.classroomName, value: item.totalAssignmentCount})),
    [classroomUsage]
  )

  const classEventPoints = useMemo(
    () =>
      aggregateBy(
        classEventAssignments,
        (item) => item.gradeLevelName,
        (item) => item.eventCount
      ).slice(0, 10),
    [classEventAssignments]
  )

  const surveyStatusSeries = [
    summary.surveyReturnedCourseCount,
    Math.max(summary.surveyAssignedCourseCount - summary.surveyReturnedCourseCount, 0),
    Math.max(summary.surveyCourseCount - summary.surveyAssignedCourseCount, 0),
  ]

  const generatedAt = data?.generatedAt
    ? new Date(data.generatedAt).toLocaleString('tr-TR')
    : '-'

  return (
    <>
      <PageTitle>Üst Ekran Raporları</PageTitle>

      <div className='d-flex flex-wrap align-items-center justify-content-between gap-3 mb-7'>
        <div>
          <h1 className='fw-bold text-gray-900 mb-1'>YKYS Üst Ekran Raporları</h1>
          <div className='text-muted fs-7'>
            {data?.semesterName || 'Dönem seçimi'} · Güncelleme: {generatedAt}
          </div>
        </div>

        <div className='d-flex align-items-center gap-3'>
          <select
            className='form-select form-select-solid min-w-225px'
            value={selectedSemesterId ?? ''}
            onChange={(event) =>
              setSelectedSemesterId(event.target.value ? Number(event.target.value) : null)
            }
          >
            <option value=''>Aktif dönem</option>
            {semesters.map((semester) => {
              const isActive = semester.isActive ?? semester.isactive
              return (
                <option key={semester.id} value={semester.id}>
                  {semester.name || `${semester.year || ''}-${semester.period || ''}`}
                  {isActive ? ' (Aktif)' : ''}
                </option>
              )
            })}
          </select>
          <button
            type='button'
            className='btn btn-light-primary'
            onClick={() => setRefreshIndex((value) => value + 1)}
            disabled={loading}
          >
            <i className='bi bi-arrow-clockwise me-2'></i>
            Yenile
          </button>
        </div>
      </div>

      {error && <div className='alert alert-danger'>{error}</div>}

      <div className='row g-5 g-xl-8 mb-8'>
        <SummaryCard
          title='Soru Bankası'
          value={formatNumber(summary.totalQuestionCount)}
          detail={`${formatNumber(summary.coursesWithQuestionCount)} derste soru var`}
          icon='bi bi-ui-checks-grid text-primary'
          tone='bg-light-primary'
        />
        <SummaryCard
          title='Ders Anketleri'
          value={formatPercent(summary.surveyResponseRate)}
          detail={`${formatNumber(summary.surveyReturnedCourseCount)} ders geri dönüş aldı`}
          icon='bi bi-chat-left-text text-success'
          tone='bg-light-success'
        />
        <SummaryCard
          title='Derslik Kullanımı'
          value={formatPercent(summary.averageClassroomUsageRate)}
          detail={`${formatNumber(summary.usedClassroomCount)} / ${formatNumber(
            summary.classroomCount
          )} derslik kullanıldı`}
          icon='bi bi-building text-warning'
          tone='bg-light-warning'
        />
        <SummaryCard
          title='Etkinlik Atamaları'
          value={formatNumber(summary.eventAssignmentCount)}
          detail={`${formatNumber(summary.courseScheduleSlotCount)} ders programı slotu`}
          icon='bi bi-calendar2-week text-danger'
          tone='bg-light-danger'
        />
      </div>

      <div className='row g-5 g-xl-8 mb-8'>
        <div className='col-xl-6'>
          <ChartCard title='Fakülte Bazlı Soru Sayısı'>
            {questionFacultyPoints.length > 0 ? (
              <ReactApexChart
                options={barOptions(questionFacultyPoints.map((item) => item.label), true)}
                series={[{name: 'Soru sayısı', data: questionFacultyPoints.map((item) => item.value)}]}
                type='bar'
                height={320}
              />
            ) : (
              <EmptyState label='Soru bankası verisi yok' />
            )}
          </ChartCard>
        </div>
        <div className='col-xl-6'>
          <ChartCard title='En Çok Sorusu Olan Dersler'>
            {topQuestionCoursePoints.length > 0 ? (
              <ReactApexChart
                options={barOptions(topQuestionCoursePoints.map((item) => item.label), true)}
                series={[
                  {name: 'Soru sayısı', data: topQuestionCoursePoints.map((item) => item.value)},
                ]}
                type='bar'
                height={320}
              />
            ) : (
              <EmptyState label='Ders kırılımı yok' />
            )}
          </ChartCard>
        </div>
      </div>

      <div className='row g-5 g-xl-8 mb-8'>
        <div className='col-xl-4'>
          <ChartCard title='Ders Anketi Geri Dönüş Durumu'>
            {surveyStatusSeries.some((value) => value > 0) ? (
              <ReactApexChart
                options={donutOptions(['Geri dönüş var', 'Geri dönüş yok', 'Atama yok'])}
                series={surveyStatusSeries}
                type='donut'
                height={315}
              />
            ) : (
              <EmptyState label='Anket verisi yok' />
            )}
          </ChartCard>
        </div>
        <div className='col-xl-4'>
          <ChartCard title='Derslik Atama Yoğunluğu'>
            {topClassroomUsagePoints.length > 0 ? (
              <ReactApexChart
                options={barOptions(topClassroomUsagePoints.map((item) => item.label), true)}
                series={[
                  {
                    name: 'Atama',
                    data: topClassroomUsagePoints.map((item) => item.value),
                  },
                ]}
                type='bar'
                height={315}
              />
            ) : (
              <EmptyState label='Derslik kullanım verisi yok' />
            )}
          </ChartCard>
        </div>
        <div className='col-xl-4'>
          <ChartCard title='Sınıf Bazlı Etkinlik Atamaları'>
            {classEventPoints.length > 0 ? (
              <ReactApexChart
                options={barOptions(classEventPoints.map((item) => item.label))}
                series={[{name: 'Etkinlik', data: classEventPoints.map((item) => item.value)}]}
                type='bar'
                height={315}
              />
            ) : (
              <EmptyState label='Etkinlik ataması yok' />
            )}
          </ChartCard>
        </div>
      </div>

      <div className='card card-bordered mb-8'>
        <div className='card-header border-0 pt-5'>
          <h3 className='card-title fw-bold text-gray-900'>Soru Bankası Kırılımı</h3>
        </div>
        <div className='card-body pt-0 table-responsive'>
          <table className='table table-row-dashed align-middle gs-0 gy-3'>
            <thead>
              <tr className='fw-bold text-muted'>
                <th>Fakülte</th>
                <th>Bölüm</th>
                <th>Ders</th>
                <th className='text-end'>Toplam</th>
                <th className='text-end'>Dönem</th>
                <th className='text-end'>Kullanılan</th>
                <th className='text-end'>Kara Liste</th>
              </tr>
            </thead>
            <tbody>
              {questionBreakdowns.map((row) => (
                <tr key={`${row.departmentId || 0}-${row.courseId}`}>
                  <td className='fw-semibold'>{row.facultyName}</td>
                  <td>{row.departmentName}</td>
                  <td>
                    <span className='fw-semibold'>{row.courseName}</span>
                    {row.courseCode && <span className='text-muted ms-2'>{row.courseCode}</span>}
                  </td>
                  <td className='text-end fw-bold'>{formatNumber(row.questionCount)}</td>
                  <td className='text-end'>{formatNumber(row.selectedSemesterQuestionCount)}</td>
                  <td className='text-end'>{formatNumber(row.usedQuestionCount)}</td>
                  <td className='text-end'>{formatNumber(row.blacklistedQuestionCount)}</td>
                </tr>
              ))}
              {questionBreakdowns.length === 0 && (
                <tr>
                  <td colSpan={7}>
                    <EmptyState label='Soru bankası kırılımı yok' />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className='card card-bordered mb-8'>
        <div className='card-header border-0 pt-5'>
          <h3 className='card-title fw-bold text-gray-900'>Ders Değerlendirme Anketleri</h3>
        </div>
        <div className='card-body pt-0 table-responsive'>
          <table className='table table-row-dashed align-middle gs-0 gy-3'>
            <thead>
              <tr className='fw-bold text-muted'>
                <th>Fakülte</th>
                <th>Bölüm</th>
                <th>Ders</th>
                <th className='text-end'>Atama</th>
                <th className='text-end'>Geri Dönüş</th>
                <th className='text-end'>Bekleyen</th>
                <th className='text-end'>Oran</th>
                <th className='text-end'>Durum</th>
              </tr>
            </thead>
            <tbody>
              {surveyCourses.map((row) => {
                const statusClass = row.hasAnyResponse
                  ? 'badge-light-success'
                  : row.totalApplications > 0
                  ? 'badge-light-warning'
                  : 'badge-light-danger'
                const statusLabel = row.hasAnyResponse
                  ? 'Geri dönüş var'
                  : row.totalApplications > 0
                  ? 'Geri dönüş yok'
                  : 'Atama yok'

                return (
                  <tr key={row.semesterCourseId}>
                    <td className='fw-semibold'>{row.facultyName}</td>
                    <td>{row.departmentName}</td>
                    <td>
                      <span className='fw-semibold'>{row.courseName}</span>
                      {row.courseCode && <span className='text-muted ms-2'>{row.courseCode}</span>}
                    </td>
                    <td className='text-end'>{formatNumber(row.totalApplications)}</td>
                    <td className='text-end fw-bold'>{formatNumber(row.completedApplications)}</td>
                    <td className='text-end'>{formatNumber(row.pendingApplications)}</td>
                    <td className='text-end'>{formatPercent(row.responseRate)}</td>
                    <td className='text-end'>
                      <span className={`badge ${statusClass}`}>{statusLabel}</span>
                    </td>
                  </tr>
                )
              })}
              {surveyCourses.length === 0 && (
                <tr>
                  <td colSpan={8}>
                    <EmptyState label='Ders anketi kırılımı yok' />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className='row g-5 g-xl-8'>
        <div className='col-xl-7'>
          <div className='card card-bordered h-100'>
            <div className='card-header border-0 pt-5'>
              <h3 className='card-title fw-bold text-gray-900'>Derslik Kullanım Oranları</h3>
            </div>
            <div className='card-body pt-0 table-responsive'>
              <table className='table table-row-dashed align-middle gs-0 gy-3'>
                <thead>
                  <tr className='fw-bold text-muted'>
                    <th>Derslik</th>
                    <th>Bina</th>
                    <th className='text-end'>Kapasite</th>
                    <th className='text-end'>Ders Slotu</th>
                    <th className='text-end'>Etkinlik</th>
                    <th className='text-end'>Kullanım</th>
                  </tr>
                </thead>
                <tbody>
                  {classroomUsage.map((row) => (
                    <tr key={row.classroomId}>
                      <td className='fw-semibold'>{row.classroomName}</td>
                      <td>{row.buildingName}</td>
                      <td className='text-end'>{formatNumber(row.capacity)}</td>
                      <td className='text-end'>{formatNumber(row.scheduledSlotCount)}</td>
                      <td className='text-end'>{formatNumber(row.eventCount)}</td>
                      <td className='text-end fw-bold'>{formatPercent(row.usageRate)}</td>
                    </tr>
                  ))}
                  {classroomUsage.length === 0 && (
                    <tr>
                      <td colSpan={6}>
                        <EmptyState label='Derslik kullanım verisi yok' />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className='col-xl-5'>
          <div className='card card-bordered h-100'>
            <div className='card-header border-0 pt-5'>
              <h3 className='card-title fw-bold text-gray-900'>Sınıf Bazlı Etkinlik Atamaları</h3>
            </div>
            <div className='card-body pt-0 table-responsive'>
              <table className='table table-row-dashed align-middle gs-0 gy-3'>
                <thead>
                  <tr className='fw-bold text-muted'>
                    <th>Fakülte</th>
                    <th>Birim</th>
                    <th>Sınıf</th>
                    <th>Derslik</th>
                    <th className='text-end'>Etkinlik</th>
                  </tr>
                </thead>
                <tbody>
                  {classEventAssignments.map((row) => (
                    <tr key={`${row.classroomId}-${row.organizationId || 0}-${row.gradeLevel || 0}`}>
                      <td className='fw-semibold'>{row.facultyName}</td>
                      <td>{row.organizationName}</td>
                      <td>{row.gradeLevelName}</td>
                      <td>{row.classroomName}</td>
                      <td className='text-end fw-bold'>{formatNumber(row.eventCount)}</td>
                    </tr>
                  ))}
                  {classEventAssignments.length === 0 && (
                    <tr>
                      <td colSpan={5}>
                        <EmptyState label='Sınıf bazlı etkinlik verisi yok' />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default ExecutiveReportDashboardPage
